<template>
    <nav class="sidebar" v-if="showComponent">
        <button v-if="showScrollButtons" @click="scrollDown(true)" id="scroll-down" class="scroll-up-button">
            <span class="arrow up"></span>
        </button>
        <div class="menu-bar" :class="{ 'with-scroll-buttons': showScrollButtons }" ref="menuBar">
            <div class="menu" ref="menu">
                <ul class="menu-links sidebar-ul">
                    <li v-for="(link, index) in authorisedLinks" :key="index" :class="{
                        'sidebar-li':true,
                        'nav-link': true,
                        'current-page': routeName === link.name || isSubPath(link.name),
                        'sub-menu-open': link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName)),
                        'router-link-sub-menu' : link.menuItems
                    }">
                        <template v-if="link.menuItems && link.menuItems.length > 0">
                            <span 
                            @click="() => { link.active = !link.active; updateScrollButtons(); }"
                            :class="{
                                'router-link-sub-menu': true,
                                'sub-menu-active-decoration': (link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName)))
                            }">
                                {{ link.name }}  
                                <span v-if="link.active || (link.menuItems && link.menuItems.some(item => item === routeName))" class="fixed-arrow-up dropdown-menu-arrow"></span>
                                <span v-if="!link.active && ! (link.menuItems && link.menuItems.some(item => item === routeName))" class="fixed-arrow-down dropdown-menu-arrow"></span>
                            </span>

                            <span v-for="(subItem, subIndex) in link.menuItems" :key="subIndex" 
                            :class="{
                                'navlink': true,
                                'sub-link' : (link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName))),
                                'invisible-sub-link': (link.active == false && !(link.menuItems && link.menuItems.some(item => item === routeName)) ) ,
                                'sub-menu-open-span': true,
                                'current-page-sub-menu': subItem === routeName, 
                                'last-sidebar-item': subIndex === link.menuItems.length - 1 && (link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName)))
                            }">
                                <span class="blue-sidebar-decoration"></span>
                                <router-link :to="{ name: subItem }" :class="{
                                    'nav-link': true,
                                    'router-link': true,
                                    'side-bar-decoration': true
                                }">
                                    <span class="sidebar-item-separation">{{ subItem }}</span>
                                </router-link>
                            </span>
                        </template>

                        <template v-else>
                            <router-link :to="{ name: link.name }" class="router-link" @click=" () => {deactivateAllLinks(); updateScrollButtons()} ">
                                <span class='main-sidebar-item'>{{ link.name }}</span>
                            </router-link>
                        </template>
                    </li>
                </ul>
            </div>
        </div>
        <button v-if="showScrollButtons" @click="scrollDown()" id="scroll-down" class="scroll-down-button">
            <span class="arrow down"></span>
        </button>
    </nav>
</template>
                        
<script setup lang="ts">
import { ref, Ref, inject, onMounted, onUnmounted, computed, nextTick } from "vue";
import { useRoute } from "vue-router";
import router from "@/router/index";
// Services
import { authService } from "@/auth/authSingleton";
import { AuthorisationLevel, GetAuthLevel } from "../config/AuthConfig";
// Misc
import log from "loglevel";
import { RoutePaths } from "@/router/RouteDetails";

const showComponent = inject("showComponent");
const route = useRoute();

const scrollAmount = ref(200);
const menuBar = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);

const scrollDown = (reverse: boolean = false) => {
    menuBar.value!.scrollBy({
        top: reverse ? -scrollAmount.value : scrollAmount.value,
        behavior: "smooth",
    });
};

const showScrollButtons = ref<boolean>(false);

onMounted(() => {
    log.trace("onMounted() SideBar");
    log.trace(`Showing links for:\nUser role: ${authService.getUsersRole()} (auth level: ${GetAuthLevel(authService.getUsersRole())})`);

    updateScrollButtons();

    window.addEventListener("resize", updateScrollButtons);
});

onUnmounted(() => {
    log.trace("onUnmounted() SideBar");
    window.removeEventListener("resize", updateScrollButtons);
});

function updateScrollButtons() {
    log.trace("updateScrollButtons() SideBar");
    if (!showComponent) return 
        nextTick(() => {
            log.trace("showComponent", showComponent);
            if (menu.value?.scrollHeight && menuBar.value?.clientHeight) {
                if (menu.value?.offsetHeight <= menuBar.value?.clientHeight) {
                    showScrollButtons.value = false;
                } else {
                    showScrollButtons.value = true;
                }
            }
        })
}

interface ILink {
    name: keyof typeof RoutePaths;
    menuItems?: Array<keyof typeof RoutePaths>; 
    active?: boolean
}
interface IAuthLevel {
    name: keyof typeof RoutePaths;
    auth: number;
}

const links: Ref<ILink[]> = ref([
    { name: 'Home' },
    { name: 'Journaling' },
    { name: 'Alerts and Warnings' },
    { name: 'EoP Verifications' },
    { name: 'Invoices' },
    { name: 'Data Processing' },
    { name: 'Loan Management' },
    { name: 'Fund Management' },
    { name: 'Consumer Extract' },
    { name: 'Reports' },
    { name: 'Customer & Contract' },
    { name: 'Hire Agreements' },
    { name: 'Origination', 
        menuItems: [
            'Contract Validation', 
            'Contract Approval', 
            'Car Purchases'
        ], 
        active:false
    },
    { name: 'Payment Confirmation', 
        menuItems: [
            'Service Invoice Payments', 
            'Recharge Invoice Payments', 
            'Churn Payments', 'VAT Payments', 
            'Confirm Interest Payments', 
            'Final Settlement Payments',
            'Payments to VCFS'
        ],
        active:false
    },
    { name: 'Sage Backup' },
    { name: 'Fees Configuration' },
    { name: 'VAT Returns' },
    { name: 'Transfer Payments' },
    { name: 'Ledger Transactions' },
    { name: 'Funding Reports' },
    { name: 'Insurance Claims' },
    { name: 'Drawdown' },
    { name: 'VAT Loans' },
    { name: 'Portfolio Forecasts' },
    { name: 'VCFS Trial Balance' }
]);
function deactivateAllLinks(){
    links.value.forEach(link => {
        if(link != undefined){
            link.active = false
        }
    })
}
const routeAuthLevels: Ref<IAuthLevel[]> = ref(
    router.getRoutes().map((route) => {
        const authLevel = route.meta?.authLevel ?? AuthorisationLevel.NoAuthorisation;
        return { name: route.name, auth: authLevel } as IAuthLevel;
    })
);

function isSubPath(name: keyof typeof RoutePaths) {
    return route.fullPath.startsWith(RoutePaths[name].replace(/\/\:(\w)+\??/g,''));
}

function IsAuthorised(link: ILink): boolean {
    let authorisedSublinks = 0;
    let indexesToRemove: number[] = [];
    let authorised;

    const matchingRoute = routeAuthLevels.value.find(
        (route: IAuthLevel) => route.name === link.name
    );
    
    if(!link.menuItems){
        authorised = !matchingRoute?.auth || matchingRoute.auth <= GetAuthLevel(authService.getUsersRole());
    }
    if (link.menuItems) {
        for (const subLink of link.menuItems) {
            let matchingAuthRoute = routeAuthLevels.value.find( route => route.name === subLink)
            if (matchingAuthRoute) { 
                const sublinkAuthorised = matchingAuthRoute.auth  <= GetAuthLevel(authService.getUsersRole());
                if (sublinkAuthorised) {
                    authorisedSublinks += 1;
                } else {
                    const indexToRemove = link.menuItems.indexOf(subLink);
                    if (indexToRemove !== -1) {
                        indexesToRemove.push(indexToRemove);
                    }
                }
            }
        }
        authorised = authorisedSublinks !== 0;
        indexesToRemove.sort((a, b) => b - a);

        for (let index of indexesToRemove) {
            link.menuItems.splice(index, 1);
        }
    }

    log.trace(`IsAuthorised()\nlink: ${link.name} (requiring auth: ${matchingRoute?.auth})\nUser auth level: ${GetAuthLevel(authService.getUsersRole())}\nAuthorised: ${authorised}`);
    return authorised;
}

const authorisedLinks: Ref<ILink[]> = ref(
    links.value.filter((link: ILink) => IsAuthorised(link))
);
const routeName = computed(()=> route.name == "Car Purchase"? "Car Purchases": route.name )
</script>

<style>
.sidebar {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100vh - var(--default-body-margin) - var(--topbar-height) - calc(var(--margin-bottom-factor) * var(--topbar-sidebar-content-spacing)) - (4 * var(--topbar-sidebar-content-spacing)));
    width: var(--sidebar-width);
    padding: 10px 10px;
    background-color: var(--background-color);
    transition: var(--tran-03);
    z-index: 100;
    box-shadow: -5px 5px 10px rgba(0, 0, 0, 0.05), 5px 5px 10px rgba(0, 0, 0, 0.05);
    border-radius: var(--topbar-sidebar-content-radius);
    margin-right: calc(3 * var(--topbar-sidebar-content-spacing));
    font-size: clamp(0.75rem, 0.75vw, 1.75rem);
}

.sidebar .menu {
    margin-top: 0px;
    margin-bottom: 0px;
}

.sidebar .menu-bar {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
}

.sidebar .menu-bar.with-scroll-buttons {
    height: calc(100% - calc(2 * 4vh) - calc(2 * 6px));
    overflow-y: scroll;
}

.menu-bar::-webkit-scrollbar {
    display: none;
    /* hide scrollbar for Chromium-based browsers (Chrome and Edge) */
}

.menu-bar {
    scrollbar-width: none;
    /* hide scrollbar for Firefox */
    -ms-overflow-style: none;
    /* hide scrollbar for IE and Edge */
}

.sidebar .text {
    color: var(--text-color);
    transition: var(--tran-03);
    font-weight: 500;
    white-space: nowrap;
    opacity: 1;
}

.sidebar-ul {
    list-style: none;
    padding: 0 0.2vw 0 0.2vw;
    margin-block-start: 0em;
    margin-block-end: 0em;
}

.sidebar-li {
    list-style: none;
    display: flex;
    align-items: center;
    color: blue;
    align-items: flex-start;
    flex-direction: column;
}

.sidebar-li:not(.current-page) {
    height: auto;
    min-height: 2.2em;
}

.sidebar-li .router-link {
    height: auto;
    min-height: 2.2em;
    list-style: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    border-radius: var(--sidebar-items-radius);
    text-decoration: none;
    transition: var(--tran-03);
    color: blue;
}

.sidebar-li .router-link:hover {
    min-height: 2.2em;
    height: auto;
}

.router-link-sub-menu{
    height: 2.2em;
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    border-radius: var(--sidebar-items-radius);
    text-decoration: none;
    color: blue;
    justify-content: center;
}

/* Problem */ 
.router-link-sub-menu span:hover{
    font-weight: bold;
    cursor: pointer;
}

.sub-menu-active-decoration{
    font-weight: bold;
    height: 2.2em;
    line-height: 1.1em;  /*  line-height: 1.1em; */
    margin-bottom: 0.3em;
}

.blue-sidebar-decoration{
    width: 6px;  
    background-color: var(--primary-color-lighter);
    display: inline-block;
    transition: all 0s ease;
}

.sub-menu-open-span .side-bar-decoration:hover::before {
    opacity: 0;
}

.sidebar-item-separation{
    padding-left: 6px;
    margin-left: 5px;
    line-height: 0.9em;
    margin-top: 0.1em;
    margin-bottom: 0.1em;
}

.sidebar-li:not(.current-page) .router-link:hover:not(.current-page-sub-menu .router-link) {
    min-height: 2.2em;
    height: auto;
    background-color: var(--primary-color);
    color: white;
    padding-left: 1em;
    width: 100%;
}


.sidebar-li router-link:hover .text {
    color: var(--sidebar-color);
}

.sidebar-li router-link:visited {
    color: inherit;
}

body.dark .sidebar-li router-link:hover .text {
    color: var(--text-color);
}

.sub-menu-open{
    height: auto !important;
    border-radius: var(--sidebar-items-radius);
}

.sub-menu-open-span {
    padding-left: 0.5em;
}

.invisible-sub-link{
    display: none;
}

.sub-link{
    min-height: 2.2em;
    width: 100% !important;
    box-sizing: content-box !important;
    display: flex;
}

.dropdown-menu-arrow{
    margin-right: 10px;
    margin-left: auto;
}

.current-page {
    height: auto;
    font-size: clamp(0.75rem, 0.9vw, 1.75rem);
    font-weight: bold;
    background-color: var(--primary-color-lighter);
    border-radius: var(--sidebar-items-radius);
    padding-left: 1em;
}

.current-page:hover{
    height: auto;
}

.main-sidebar-item{
    height: auto;
    min-height: 2.2em;
    display: flex;
    align-items: center;
    line-height: 1.1em;
    padding-top: 0.5em;
    padding-bottom: 0.5em;
}

.current-page-sub-menu .sidebar-item-separation {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2.2em;
    line-height: 1.1;
    font-size: clamp(0.75rem, 0.9vw, 1.75rem);
    font-weight: bold;
    background-color: var(--primary-color-lighter);
    border-radius: var(--sidebar-items-radius);
    width: 100%;
    padding-left: 10px;
    text-align: left;
}

.arrow {
    border: solid var(--primary-color);
    border-width: 0 3px 3px 0;
    display: inline-block;
    padding: 3px;
}

.fixed-arrow-down {
    border: solid var(--primary-color);
    border-width: 0 3px 3px 0;
    display: inline-block;
    padding: 3px;
    transition: transform 0.3s;
    transform: rotate(45deg);
}

.fixed-arrow-up {
    border: solid var(--primary-color);
    border-width: 0 3px 3px 0;
    display: inline-block;
    padding: 3px;
    transition: transform 0.3s;
    transform: rotate(-135deg);
}

.down {
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
}

.up {
    transform: rotate(-135deg);
    -webkit-transform: rotate(-135deg);
}

.scroll-up-button {
    position: absolute;
    background-color: var(--background-color);
    border: none;
    cursor: pointer;
    z-index: 9999;
    width: calc(100% - calc(2 * 10px));
    top: 0;
    margin-top: 6px;
}

.scroll-up-button span {
    display: inline-block;
    vertical-align: middle;
}

.scroll-up-button:hover .arrow {
    padding: 6px;
    border-width: 0 6px 6px 0;
}

.scroll-down-button {
    position: absolute;
    background-color: var(--background-color);
    border: none;
    cursor: pointer;
    z-index: 9999;
    width: calc(100% - calc(2 * 10px));
    bottom: 0;
    margin-bottom: 6px;
}

.scroll-down-button span {
    display: inline-block;
    vertical-align: middle;
}

.scroll-down-button:hover .arrow {
    padding: 6px;
    border-width: 0 6px 6px 0;
}
.last-sidebar-item {
    margin-bottom: 10px;
  }
</style>